/**
 * Проверка черновика по правилам Discord. Используется и в панели (живые подсказки),
 * и на сервере перед отправкой, чтобы не получать 400 от API.
 */
import { LIMITS, FORBIDDEN_WEBHOOK_NAMES } from './limits.js';
import type { DActionRow, DButton, DEmbed, DSelect, DV2Child, DV2Top, Draft } from './types.js';

export interface Issue {
  level: 'error' | 'warning';
  /** человекочитаемое место: «Эмбед 1 -> Поле 2» */
  where: string;
  /** id элемента черновика, чтобы панель могла подсветить его */
  itemId?: string;
  message: string;
}

/** Длина в символах так, как её считает Discord (кодовые точки). */
export function len(s: string | undefined | null): number {
  if (!s) return 0;
  let n = 0;
  for (const _ of s) n++;
  return n;
}

const URL_RE = /^https?:\/\/[^\s]+$/i;
const MEDIA_RE = /^(https?:\/\/[^\s]+|attachment:\/\/[^\s]+|upload:\/\/[^\s]+)$/i;

export function isUrl(s: string): boolean {
  return URL_RE.test(s.trim());
}

export function isMediaUrl(s: string): boolean {
  return MEDIA_RE.test(s.trim());
}

export function embedLength(e: DEmbed): number {
  return (
    len(e.title) +
    len(e.description) +
    len(e.footer.text) +
    len(e.author.name) +
    e.fields.reduce((s, f) => s + len(f.name) + len(f.value), 0)
  );
}

export function isEmbedEmpty(e: DEmbed): boolean {
  return (
    !e.title.trim() &&
    !e.description.trim() &&
    !e.author.name.trim() &&
    !e.footer.text.trim() &&
    !e.image.trim() &&
    !e.thumbnail.trim() &&
    e.fields.length === 0
  );
}

export function countV2Components(list: (DV2Top | DV2Child)[]): number {
  let n = 0;
  for (const c of list) {
    n += 1;
    if (c.type === 'container') n += countV2Components(c.children);
    else if (c.type === 'section') n += c.texts.length + 1;
    else if (c.type === 'row') n += c.items.length;
  }
  return n;
}

export function v2TextLength(list: (DV2Top | DV2Child)[]): number {
  let n = 0;
  for (const c of list) {
    if (c.type === 'text') n += len(c.content);
    else if (c.type === 'section') n += c.texts.reduce((s, t) => s + len(t.content), 0);
    else if (c.type === 'container') n += v2TextLength(c.children);
  }
  return n;
}

class Collector {
  issues: Issue[] = [];
  err(where: string, message: string, itemId?: string) {
    this.issues.push({ level: 'error', where, message, itemId });
  }
  warn(where: string, message: string, itemId?: string) {
    this.issues.push({ level: 'warning', where, message, itemId });
  }
  max(where: string, value: string, limit: number, label: string, itemId?: string) {
    const l = len(value);
    if (l > limit) this.err(where, `${label}: ${l} из ${limit} символов`, itemId);
  }
}

function validateEmoji(c: Collector, where: string, emoji: string, itemId?: string) {
  const v = emoji.trim();
  if (!v) return;
  if (/^<a?:/.test(v) && !/^<(a?):([\w~]{1,32}):(\d{15,25})>$/.test(v)) {
    c.err(where, 'Кастомное эмодзи должно быть в формате <:name:id> или <a:name:id>', itemId);
  } else if (!v.startsWith('<') && len(v) > 8) {
    c.err(where, 'В поле эмодзи должен быть один юникод-символ или кастомное эмодзи', itemId);
  }
}

function validateButton(c: Collector, where: string, b: DButton, customIds: Set<string>) {
  if (!b.label.trim() && !b.emoji.trim()) c.err(where, 'У кнопки должен быть текст или эмодзи', b.id);
  c.max(where, b.label, LIMITS.buttonLabel, 'Текст кнопки', b.id);
  validateEmoji(c, where, b.emoji, b.id);
  if (b.style === 'link') {
    if (!b.url.trim()) c.err(where, 'Для кнопки-ссылки нужна ссылка', b.id);
    else if (!/^(https?|discord):\/\/\S+$/i.test(b.url.trim())) c.err(where, 'Ссылка должна начинаться с https://', b.id);
    else c.max(where, b.url, LIMITS.linkUrl, 'Ссылка кнопки', b.id);
  } else {
    if (b.action.type === 'template' && !b.action.templateId) c.err(where, 'Выберите шаблон для ответа', b.id);
    if (b.action.type === 'role' && !b.action.roleId) c.err(where, 'Выберите роль', b.id);
    if (b.action.type === 'raw') {
      if (!b.action.customId) c.err(where, 'Пустой custom_id', b.id);
      else {
        c.max(where, b.action.customId, LIMITS.customId, 'custom_id', b.id);
        if (customIds.has(b.action.customId)) c.err(where, 'custom_id повторяется - Discord требует уникальные', b.id);
        customIds.add(b.action.customId);
      }
      c.warn(where, 'Кнопка другого бота: Embed Studio не ответит на нажатие, сообщение должен опубликовать тот бот', b.id);
    }
  }
}

function validateSelect(c: Collector, where: string, s: DSelect, customIds: Set<string>) {
  if (s.options.length === 0) c.err(where, 'В меню должен быть хотя бы один вариант', s.id);
  if (s.options.length > LIMITS.selectOptions) c.err(where, `Максимум ${LIMITS.selectOptions} вариантов`, s.id);
  c.max(where, s.placeholder, LIMITS.selectPlaceholder, 'Плейсхолдер', s.id);
  if (s.minValues < 0 || s.minValues > 25) c.err(where, 'Минимум выбора - от 0 до 25', s.id);
  if (s.maxValues < 1 || s.maxValues > 25) c.err(where, 'Максимум выбора - от 1 до 25', s.id);
  if (s.minValues > s.maxValues) c.err(where, 'Минимум выбора больше максимума', s.id);
  if (s.maxValues > s.options.length && s.options.length > 0)
    c.err(where, 'Максимум выбора больше количества вариантов', s.id);
  if (s.rawCustomId) {
    if (customIds.has(s.rawCustomId)) c.err(where, 'custom_id повторяется', s.id);
    customIds.add(s.rawCustomId);
  }
  const defaults = s.options.filter((o) => o.isDefault).length;
  if (defaults > s.maxValues) c.err(where, 'Вариантов по умолчанию больше, чем можно выбрать', s.id);
  s.options.forEach((o, i) => {
    const w = `${where} -> Вариант ${i + 1}`;
    if (!o.label.trim()) c.err(w, 'Пустое название варианта', s.id);
    c.max(w, o.label, LIMITS.optionLabel, 'Название', s.id);
    c.max(w, o.description, LIMITS.optionDescription, 'Описание', s.id);
    validateEmoji(c, w, o.emoji, s.id);
    if (o.action.type === 'template' && !o.action.templateId) c.err(w, 'Выберите шаблон', s.id);
    if (o.action.type === 'role' && !o.action.roleId) c.err(w, 'Выберите роль', s.id);
  });
}

function validateRow(c: Collector, where: string, row: DActionRow, customIds: Set<string>) {
  if (row.items.length === 0) {
    c.err(where, 'Пустой ряд компонентов', row.id);
    return;
  }
  const selects = row.items.filter((i) => i.type === 'select').length;
  if (selects > 0 && row.items.length > 1) c.err(where, 'Меню выбора должно быть единственным элементом ряда', row.id);
  if (row.items.length > LIMITS.buttonsPerRow) c.err(where, `Максимум ${LIMITS.buttonsPerRow} кнопок в ряду`, row.id);
  row.items.forEach((it, i) => {
    const w = `${where} -> ${it.type === 'button' ? 'Кнопка' : 'Меню'} ${i + 1}`;
    if (it.type === 'button') validateButton(c, w, it, customIds);
    else validateSelect(c, w, it, customIds);
  });
}

function validateEmbed(c: Collector, i: number, e: DEmbed) {
  const w = `Эмбед ${i + 1}`;
  if (isEmbedEmpty(e)) c.err(w, 'Эмбед пустой - заполните хотя бы заголовок, описание или картинку', e.id);
  c.max(w, e.title, LIMITS.embedTitle, 'Заголовок', e.id);
  c.max(w, e.description, LIMITS.embedDescription, 'Описание', e.id);
  c.max(w, e.author.name, LIMITS.authorName, 'Автор', e.id);
  c.max(w, e.footer.text, LIMITS.footerText, 'Футер', e.id);
  if (e.url && !isUrl(e.url)) c.err(w, 'Ссылка заголовка должна начинаться с https://', e.id);
  if (e.url && !e.title) c.warn(w, 'Ссылка без заголовка видна только как группировка картинок', e.id);
  if (e.author.url && !isUrl(e.author.url)) c.err(w, 'Ссылка автора должна начинаться с https://', e.id);
  if ((e.author.url || e.author.iconUrl) && !e.author.name) c.err(w, 'Иконка/ссылка автора без имени автора не отображается', e.id);
  if (e.author.iconUrl && !isMediaUrl(e.author.iconUrl)) c.err(w, 'Некорректная ссылка на иконку автора', e.id);
  if (e.footer.iconUrl && !e.footer.text) c.err(w, 'Иконка футера без текста футера не отображается', e.id);
  if (e.footer.iconUrl && !isMediaUrl(e.footer.iconUrl)) c.err(w, 'Некорректная ссылка на иконку футера', e.id);
  if (e.image && !isMediaUrl(e.image)) c.err(w, 'Некорректная ссылка на изображение', e.id);
  if (e.thumbnail && !isMediaUrl(e.thumbnail)) c.err(w, 'Некорректная ссылка на миниатюру', e.id);
  if (e.timestamp && Number.isNaN(Date.parse(e.timestamp))) c.err(w, 'Некорректная дата', e.id);
  if (e.color !== null && (e.color < 0 || e.color > 0xffffff)) c.err(w, 'Цвет вне диапазона', e.id);
  if (e.fields.length > LIMITS.embedFields) c.err(w, `Максимум ${LIMITS.embedFields} полей`, e.id);
  e.fields.forEach((f, j) => {
    const fw = `${w} -> Поле ${j + 1}`;
    if (!f.name.trim()) c.err(fw, 'Название поля не может быть пустым (используйте \\u200b для «пустого»)', e.id);
    if (!f.value.trim()) c.err(fw, 'Значение поля не может быть пустым', e.id);
    c.max(fw, f.name, LIMITS.fieldName, 'Название', e.id);
    c.max(fw, f.value, LIMITS.fieldValue, 'Значение', e.id);
  });
}

function validateV2Child(
  c: Collector,
  where: string,
  ch: DV2Child,
  draft: Draft,
  customIds: Set<string>,
  usedFiles: Set<string>,
) {
  switch (ch.type) {
    case 'text':
      if (!ch.content.trim()) c.err(where, 'Пустой текстовый блок', ch.id);
      break;
    case 'section':
      if (ch.texts.length < 1 || ch.texts.length > LIMITS.sectionTexts)
        c.err(where, `В секции от 1 до ${LIMITS.sectionTexts} текстовых блоков`, ch.id);
      ch.texts.forEach((t, i) => {
        if (!t.content.trim()) c.err(`${where} -> Текст ${i + 1}`, 'Пустой текстовый блок', ch.id);
      });
      if (ch.accessory.type === 'thumbnail') {
        if (!ch.accessory.url.trim()) c.err(where, 'Укажите картинку миниатюры', ch.id);
        else if (!isMediaUrl(ch.accessory.url)) c.err(where, 'Некорректная ссылка миниатюры', ch.id);
        c.max(where, ch.accessory.description, LIMITS.mediaDescription, 'Описание миниатюры', ch.id);
      } else {
        validateButton(c, `${where} -> Кнопка`, ch.accessory, customIds);
      }
      break;
    case 'gallery':
      if (ch.items.length < 1 || ch.items.length > LIMITS.galleryItems)
        c.err(where, `В галерее от 1 до ${LIMITS.galleryItems} элементов`, ch.id);
      ch.items.forEach((it, i) => {
        if (!it.url.trim()) c.err(`${where} -> Медиа ${i + 1}`, 'Укажите ссылку', ch.id);
        else if (!isMediaUrl(it.url)) c.err(`${where} -> Медиа ${i + 1}`, 'Некорректная ссылка', ch.id);
        c.max(`${where} -> Медиа ${i + 1}`, it.description, LIMITS.mediaDescription, 'Описание', ch.id);
      });
      break;
    case 'file':
      if (!ch.attachment) c.err(where, 'Выберите вложение для файла', ch.id);
      else if (!draft.attachments.some((a) => a.name === ch.attachment))
        c.err(where, `Вложение «${ch.attachment}» не найдено - добавьте его в разделе «Вложения»`, ch.id);
      usedFiles.add(ch.attachment);
      break;
    case 'separator':
      break;
    case 'row':
      validateRow(c, where, ch, customIds);
      break;
  }
}

const V2_NAMES: Record<string, string> = {
  container: 'Контейнер',
  text: 'Текст',
  section: 'Секция',
  gallery: 'Галерея',
  file: 'Файл',
  separator: 'Разделитель',
  row: 'Ряд кнопок',
};

export interface ValidateContext {
  /** проверка для редактирования существующего сообщения */
  edit?: { wasV2: boolean };
  target?: 'bot' | 'webhook' | 'interaction';
}

export function validateDraft(draft: Draft, ctx: ValidateContext = {}): Issue[] {
  const c = new Collector();
  const customIds = new Set<string>();

  if (draft.attachments.length > LIMITS.attachments) c.err('Вложения', `Максимум ${LIMITS.attachments} файлов`);
  const names = new Set<string>();
  draft.attachments.forEach((a, i) => {
    const w = `Вложение ${i + 1}`;
    if (!a.name.trim()) c.err(w, 'Пустое имя файла', a.id);
    if (names.has(a.name)) c.err(w, `Имя «${a.name}» повторяется`, a.id);
    names.add(a.name);
    if (!a.source.trim()) c.err(w, 'Не указан источник файла', a.id);
    else if (!/^(https?:\/\/|upload:\/\/)/i.test(a.source)) c.err(w, 'Источник файла - https:// ссылка или загрузка', a.id);
    c.max(w, a.description, LIMITS.attachmentDescription, 'Описание', a.id);
  });

  if (ctx.target === 'webhook' && draft.profile.username) {
    c.max('Профиль', draft.profile.username, LIMITS.webhookUsername, 'Имя');
    const lower = draft.profile.username.toLowerCase();
    if (FORBIDDEN_WEBHOOK_NAMES.some((n) => lower.includes(n))) c.err('Профиль', 'Имя вебхука не может содержать «discord» или «clyde»');
  }
  if (draft.profile.avatarUrl && !isUrl(draft.profile.avatarUrl)) c.err('Профиль', 'Аватар должен быть https:// ссылкой');

  if (draft.mode === 'classic') {
    c.max('Текст сообщения', draft.content, LIMITS.content, 'Текст');
    if (draft.embeds.length > LIMITS.embedsPerMessage) c.err('Эмбеды', `Максимум ${LIMITS.embedsPerMessage} эмбедов`);
    draft.embeds.forEach((e, i) => validateEmbed(c, i, e));
    const total = draft.embeds.reduce((s, e) => s + embedLength(e), 0);
    if (total > LIMITS.embedTotal) c.err('Эмбеды', `Суммарно ${total} из ${LIMITS.embedTotal} символов во всех эмбедах`);

    if (draft.rows.length > LIMITS.actionRows) c.err('Компоненты', `Максимум ${LIMITS.actionRows} рядов`);
    draft.rows.forEach((r, i) => validateRow(c, `Ряд ${i + 1}`, r, customIds));

    if (draft.poll) {
      if (ctx.edit) c.err('Опрос', 'Опрос нельзя добавить или изменить при редактировании - Discord это запрещает');
      const p = draft.poll;
      if (!p.question.trim()) c.err('Опрос', 'Пустой вопрос');
      c.max('Опрос', p.question, LIMITS.pollQuestion, 'Вопрос');
      if (p.answers.length < 1 || p.answers.length > LIMITS.pollAnswers) c.err('Опрос', `От 1 до ${LIMITS.pollAnswers} ответов`);
      p.answers.forEach((a, i) => {
        if (!a.text.trim()) c.err(`Опрос -> Ответ ${i + 1}`, 'Пустой ответ');
        c.max(`Опрос -> Ответ ${i + 1}`, a.text, LIMITS.pollAnswerText, 'Ответ');
        validateEmoji(c, `Опрос -> Ответ ${i + 1}`, a.emoji);
      });
      if (p.durationHours < 1 || p.durationHours > LIMITS.pollMaxHours)
        c.err('Опрос', `Длительность от 1 до ${LIMITS.pollMaxHours} часов (32 дня)`);
      if (ctx.target === 'interaction') c.warn('Опрос', 'Опросы в эфемерных ответах не поддерживаются');
    }

    const hasBody =
      draft.content.trim() || draft.embeds.length > 0 || draft.attachments.length > 0 || (draft.poll && !ctx.edit);
    if (!hasBody) {
      c.err('Сообщение', draft.rows.length ? 'Компоненты нельзя отправить без текста, эмбеда или файла' : 'Сообщение пустое');
    }
  } else {
    if (ctx.edit === undefined && draft.content.trim()) {
      c.warn('Текст сообщения', 'В режиме Components V2 поле content не используется - добавьте текстовый блок');
    }
    if (draft.v2.length === 0) c.err('Компоненты V2', 'Добавьте хотя бы один компонент');
    const usedFiles = new Set<string>();
    draft.v2.forEach((top, i) => {
      const w = `${V2_NAMES[top.type]} ${i + 1}`;
      if (top.type === 'container') {
        if (top.children.length === 0) c.err(w, 'Пустой контейнер', top.id);
        if (top.accent !== null && (top.accent < 0 || top.accent > 0xffffff)) c.err(w, 'Цвет вне диапазона', top.id);
        top.children.forEach((ch, j) =>
          validateV2Child(c, `${w} -> ${V2_NAMES[ch.type]} ${j + 1}`, ch, draft, customIds, usedFiles),
        );
      } else {
        validateV2Child(c, w, top, draft, customIds, usedFiles);
      }
    });
    const total = countV2Components(draft.v2);
    if (total > LIMITS.v2Components) c.err('Компоненты V2', `Компонентов ${total} из ${LIMITS.v2Components} (считая вложенные)`);
    const text = v2TextLength(draft.v2);
    if (text > LIMITS.v2TextTotal) c.err('Компоненты V2', `Текста ${text} из ${LIMITS.v2TextTotal} символов суммарно`);
    for (const a of draft.attachments) {
      const referencedInMedia = JSON.stringify(draft.v2).includes(`attachment://${a.name}`);
      if (!usedFiles.has(a.name) && !referencedInMedia) {
        c.warn(`Вложение «${a.name}»`, 'В режиме V2 файл виден только если на него ссылается компонент «Файл» или медиа');
      }
    }
    if (ctx.edit && !ctx.edit.wasV2) {
      c.warn('Редактирование', 'Сообщение будет переведено в Components V2 - вернуть классический вид потом нельзя');
    }
  }

  if (ctx.edit?.wasV2 && draft.mode === 'classic') {
    c.err('Редактирование', 'Сообщение уже в формате Components V2 - Discord не позволяет вернуть его в классический вид');
  }

  return c.issues;
}

export function hasErrors(issues: Issue[]): boolean {
  return issues.some((i) => i.level === 'error');
}
